package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PeopleKpis
import br.com.vvrefeicoes.portal.domain.model.PeopleMonth
import br.com.vvrefeicoes.portal.domain.model.PeopleSnapshot
import br.com.vvrefeicoes.portal.domain.model.PeopleUnit
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.PeopleRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemotePeopleRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : PeopleRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): PeopleSnapshot {
        val path = "/operational/people?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<PeopleDto>().toDomain(selection.endMonth)
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class PeopleDto(
    val months: List<PanelMonthDto> = emptyList(),
    val kpis: PeopleKpisDto = PeopleKpisDto(),
    val monthly: List<PeopleMonthDto> = emptyList(),
    val units: List<PeopleUnitDto> = emptyList(),
)

@Serializable
private data class PeopleKpisDto(
    val fopagCents: Long = 0,
    val variableCostsCents: Long = 0,
    val turnoverPercent: Double = 0.0,
    val overtimeCents: Long = 0,
    val bonusCents: Long = 0,
    val absenteeismPercent: Double = 0.0,
)

@Serializable
private data class PeopleMonthDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val fopagCents: Long = 0,
    val overtimeCents: Long = 0,
    val bonusCents: Long = 0,
    val absenteeismPercent: Double = 0.0,
    val hires: Int = 0,
    val terminations: Int = 0,
    val turnoverPercent: Double = 0.0,
)

@Serializable
private data class PeopleUnitDto(
    val unitId: String = "",
    val unitName: String = "",
    val employees: Int = 0,
    val vacancies: Int = 0,
    val fopagCents: Long = 0,
    val programmedHours: Int = 0,
    val absentHours: Int = 0,
    val absenteeismPercent: Double = 0.0,
    val hires: Int = 0,
    val terminations: Int = 0,
    val effective: Int = 0,
    val turnoverPercent: Double = 0.0,
    val overtimeCents: Long = 0,
    val nightShiftCents: Long = 0,
    val bonusCents: Long = 0,
)

private fun PeopleDto.toDomain(period: MonthPeriod): PeopleSnapshot =
    PeopleSnapshot(
        period = period,
        kpis =
            PeopleKpis(
                fopagCents = kpis.fopagCents,
                variableCostsCents = kpis.variableCostsCents,
                turnoverPercent = kpis.turnoverPercent,
                overtimeCents = kpis.overtimeCents,
                bonusCents = kpis.bonusCents,
                absenteeismPercent = kpis.absenteeismPercent,
            ),
        months = months.map { MonthPeriod(it.year, it.month) },
        monthly =
            monthly.map {
                PeopleMonth(
                    period = MonthPeriod(it.period.year, it.period.month),
                    fopagCents = it.fopagCents,
                    overtimeCents = it.overtimeCents,
                    bonusCents = it.bonusCents,
                    absenteeismPercent = it.absenteeismPercent,
                    hires = it.hires,
                    terminations = it.terminations,
                    turnoverPercent = it.turnoverPercent,
                )
            },
        units =
            units.map {
                PeopleUnit(
                    unitId = it.unitId,
                    unitName = it.unitName,
                    employees = it.employees,
                    vacancies = it.vacancies,
                    fopagCents = it.fopagCents,
                    programmedHours = it.programmedHours,
                    absentHours = it.absentHours,
                    absenteeismPercent = it.absenteeismPercent,
                    hires = it.hires,
                    terminations = it.terminations,
                    effective = it.effective,
                    turnoverPercent = it.turnoverPercent,
                    overtimeCents = it.overtimeCents,
                    nightShiftCents = it.nightShiftCents,
                    bonusCents = it.bonusCents,
                )
            },
    )
